import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  BugReport as DetectionIcon,
  FolderOpen as CaseIcon,
  Warning as IncidentIcon,
  Security as SecurityIcon,
  Storage as AssetIcon,
  Settings as PropertiesIcon,
  Calculate as RecomputeIcon,
  Devices as DevicesIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePermission } from '../hooks/usePermission';
import { formatDateTime } from '../utils/dateUtils';
import { EntityDialog, EMPTY_ENTITY_OPTIONS, getEntityErrorMessage } from '../components/Entities/EntityDialog';

const entityStatusColors = {
  Active: '#4caf50',
  Inactive: '#757575',
  Compromised: '#d32f2f',
  'Under Investigation': '#ff9800',
  Quarantined: '#1976d2',
};

const getRiskColor = (score) => {
  if (score >= 80) return '#d32f2f';
  if (score >= 60) return '#ff9800';
  if (score >= 40) return '#ffc107';
  return '#4caf50';
};

// Theme-aware risk band used for the recompute-able Risk summary (distinct
// thresholds from getRiskColor, which drives the four-tier legend elsewhere).
const riskBandColor = (score) => (score < 30 ? 'success.main' : score < 60 ? 'warning.main' : 'error.main');

// Types that represent a physical/virtual host vs. a person for the
// user<->device association cards below.
const HOST_ENTITY_TYPES = new Set([
  'Computer/Host',
  'Server',
  'Workstation',
  'Hostname',
  'Virtual Machine',
  'Domain Controller',
]);
const USER_ENTITY_TYPE = 'User Account';
const isHostEntityType = (type) => HOST_ENTITY_TYPES.has(type);

const RELATIONSHIP_LABELS = {
  last_logged_on_user: 'Last logged-on user',
  logs_on_to: 'Logs on to',
  user_seen_on_host: 'Seen in detection',
  seen_on_host: 'Seen on host',
};

const relationshipLabel = (relationship) => {
  if (!relationship) return '-';
  return RELATIONSHIP_LABELS[relationship] || String(relationship).replace(/_/g, ' ');
};

const personName = (p) => {
  if (!p) return '';
  if (typeof p === 'string') return '';
  const full = [p.firstName, p.lastName].filter(Boolean).join(' ');
  return full || p.username || p.email || '';
};

const humanize = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

const isEmptyValue = (v) =>
  v === null || v === undefined || v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date) && Object.keys(v).length === 0);

const looksLikeDate = (key, value) =>
  typeof value === 'string' && /(At|Date|Seen|Time)$/i.test(key) && !Number.isNaN(Date.parse(value));

/**
 * Generic key/value renderer for the loosely-typed properties / assetInfo /
 * securityInfo sub-documents. Nested objects are flattened one level; anything
 * deeper is shown as JSON.
 */
const KeyValueGrid = ({ data, fmt }) => {
  const entries = Object.entries(data || {}).filter(([k, v]) => !k.startsWith('_') && !isEmptyValue(v));
  if (entries.length === 0) {
    return <Typography variant="body2" color="text.secondary">No information recorded.</Typography>;
  }
  const renderValue = (key, value) => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) {
      return (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {value.map((item, i) => (
            <Chip key={i} size="small" variant="outlined" label={typeof item === 'string' ? item : JSON.stringify(item)} />
          ))}
        </Box>
      );
    }
    if (typeof value === 'object') {
      return (
        <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(value, null, 2)}
        </Box>
      );
    }
    if (looksLikeDate(key, value)) return fmt(value);
    return String(value);
  };
  return (
    <Grid container spacing={2}>
      {entries.map(([key, value]) => (
        <Grid item xs={12} sm={6} key={key}>
          <Typography variant="caption" color="text.secondary" display="block">
            {humanize(key)}
          </Typography>
          <Typography component="div" variant="body2" sx={{ wordBreak: 'break-word' }}>
            {renderValue(key, value)}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
};

const DetailRow = ({ label, children, mono = false }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined} sx={{ wordBreak: 'break-all' }}>
      {children === undefined || children === null || children === '' ? '-' : children}
    </Typography>
  </Grid>
);

// Compact risk indicator (bar + number) used inside association tables.
const MiniRisk = ({ score }) => {
  const s = Math.min(100, Math.max(0, Number(score) || 0));
  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 90 }}>
      <LinearProgress
        variant="determinate"
        value={s}
        sx={{
          flexGrow: 1,
          height: 6,
          borderRadius: 3,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': { bgcolor: riskBandColor(s) },
        }}
      />
      <Typography variant="caption" sx={{ minWidth: 20, textAlign: 'right' }}>{s}</Typography>
    </Box>
  );
};

const EntityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getSeverityColor, getStatusColor } = useSettings();
  const { hasPermission } = usePermission();

  const canUpdate = hasPermission('entities', 'update');
  const canDelete = hasPermission('entities', 'delete');

  const [entity, setEntity] = useState(null);
  const [options, setOptions] = useState(EMPTY_ENTITY_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [recomputingRisk, setRecomputingRisk] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Add-incident form
  const [incidentForm, setIncidentForm] = useState({ type: '', severity: 'Medium', description: '' });
  const [addingIncident, setAddingIncident] = useState(false);
  const [incidentError, setIncidentError] = useState('');

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const fmt = (d) => (d ? formatDateTime(d, user?.preferences) : '-');

  const statusColorFor = (status) => {
    const fromSettings = getStatusColor(status);
    const main = typeof fromSettings === 'string' ? fromSettings : fromSettings?.main;
    if (main && main !== '#757575') return main;
    return entityStatusColors[status] || '#757575';
  };

  const fetchEntity = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.entities.getById(id);
      setEntity(response.data.entity);
    } catch (err) {
      console.error('Failed to fetch entity:', err);
      setError(err.response?.status === 404 ? 'Entity not found' : 'Failed to load entity');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEntity();
    apiService.entities.getOptions()
      .then((res) => setOptions({ ...EMPTY_ENTITY_OPTIONS, ...res.data }))
      .catch((err) => console.error('Failed to load entity options:', err));
  }, [fetchEntity]);

  const handleAddIncident = async () => {
    if (!incidentForm.type.trim() || !incidentForm.description.trim()) {
      setIncidentError('Type and description are required');
      return;
    }
    try {
      setAddingIncident(true);
      setIncidentError('');
      await apiService.entities.addIncident(id, {
        type: incidentForm.type.trim(),
        severity: incidentForm.severity,
        description: incidentForm.description.trim(),
      });
      setIncidentForm({ type: '', severity: 'Medium', description: '' });
      notify('Incident added');
      fetchEntity();
    } catch (err) {
      console.error('Failed to add incident:', err);
      setIncidentError(getEntityErrorMessage(err, 'Failed to add incident'));
    } finally {
      setAddingIncident(false);
    }
  };

  const handleRecomputeRisk = async () => {
    try {
      setRecomputingRisk(true);
      const response = await apiService.entities.recomputeEntityRisk(id);
      if (response.data?.entity) setEntity(response.data.entity);
      notify('Risk recomputed');
    } catch (err) {
      console.error('Failed to recompute entity risk:', err);
      notify(getEntityErrorMessage(err, 'Failed to recompute risk'), 'error');
    } finally {
      setRecomputingRisk(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apiService.entities.delete(id);
      navigate('/dashboard/entities');
    } catch (err) {
      console.error('Failed to delete entity:', err);
      notify(getEntityErrorMessage(err, 'Failed to delete entity'), 'error');
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !entity) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/entities')} sx={{ mb: 2 }}>
          Back to Entities
        </Button>
        <Alert severity="error">{error || 'Entity not found'}</Alert>
      </Box>
    );
  }

  const riskScore = Math.min(100, Math.max(0, Number(entity.riskScore) || 0));
  const incidents = [...(entity.incidents || [])].sort(
    (a, b) => new Date(b.occurredAt || b.createdAt || 0) - new Date(a.occurredAt || a.createdAt || 0)
  );
  const detections = entity.detections || [];
  const cases = entity.cases || [];
  const relatedEntities = (entity.relatedEntities || []).filter((r) => r?.entity);
  const isUserEntity = entity.type === USER_ENTITY_TYPE;
  const isHostEntity = isHostEntityType(entity.type);
  const associatedDevices = isUserEntity ? relatedEntities.filter((r) => isHostEntityType(r.entity.type)) : [];
  const associatedUsers = isHostEntity ? relatedEntities.filter((r) => r.entity.type === USER_ENTITY_TYPE) : [];
  const shownRelatedIds = new Set([...associatedDevices, ...associatedUsers].map((r) => r.entity._id));
  const genericRelatedEntities = relatedEntities.filter((r) => !shownRelatedIds.has(r.entity._id));
  const edrCoverageEntries = Object.entries(entity.securityInfo?.edr || {}).filter(([, v]) => v);
  const owner = entity.owner || entity.assetInfo?.owner || '';

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" component="button" onClick={() => navigate('/dashboard')}>
          Dashboard
        </Link>
        <Link underline="hover" color="inherit" component="button" onClick={() => navigate('/dashboard/entities')}>
          Entities
        </Link>
        <Typography color="text.primary">{entity.name}</Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            {entity.type}
          </Typography>
          <Typography variant="h5" gutterBottom>
            {entity.name}
          </Typography>
          <Typography variant="body2" fontFamily="monospace" color="text.secondary" sx={{ wordBreak: 'break-all', mb: 1 }}>
            {entity.identifier}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label={`Criticality: ${entity.criticality || 'Unknown'}`}
              size="small"
              sx={{ backgroundColor: getSeverityColor(entity.criticality).main, color: 'white' }}
            />
            <Chip
              label={entity.status || 'Unknown'}
              size="small"
              variant="outlined"
              sx={{ borderColor: statusColorFor(entity.status), color: statusColorFor(entity.status) }}
            />
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/entities')}>
            Back
          </Button>
          {canUpdate && (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}>
              Delete
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Details</Typography>
              <Grid container spacing={2}>
                <DetailRow label="Type">{entity.type}</DetailRow>
                <DetailRow label="Identifier" mono>{entity.identifier}</DetailRow>
                <DetailRow label="Owner">{owner}</DetailRow>
                <DetailRow label="Location">{entity.location}</DetailRow>
                <DetailRow label="Criticality">{entity.criticality}</DetailRow>
                <DetailRow label="Status">{entity.status}</DetailRow>
                <DetailRow label="Created">
                  {fmt(entity.createdAt)}{personName(entity.createdBy) ? ` by ${personName(entity.createdBy)}` : ''}
                </DetailRow>
                <DetailRow label="Last Modified">
                  {fmt(entity.updatedAt)}{personName(entity.lastModifiedBy) ? ` by ${personName(entity.lastModifiedBy)}` : ''}
                </DetailRow>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {entity.description || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Tags
                  </Typography>
                  {entity.tags?.length ? (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {entity.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {isUserEntity && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <DevicesIcon color="action" />
                  <Typography variant="h6">Associated devices ({associatedDevices.length})</Typography>
                </Box>
                {associatedDevices.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No devices associated yet. Devices are linked from EDR inventory logons and from detections.
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Device</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>IP</TableCell>
                          <TableCell>OS</TableCell>
                          <TableCell>EDR</TableCell>
                          <TableCell>Risk</TableCell>
                          <TableCell>Relationship</TableCell>
                          <TableCell>Since</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {associatedDevices.map((r, i) => {
                          const dev = r.entity;
                          const edrList = Object.values(dev.securityInfo?.edr || {}).filter(Boolean);
                          const isolated = edrList.some((e) => e.isolated);
                          return (
                            <TableRow key={dev._id || i}>
                              <TableCell>
                                <Link
                                  component="button"
                                  underline="hover"
                                  onClick={() => navigate(`/dashboard/entities/${dev._id}`)}
                                >
                                  {dev.name || dev.identifier}
                                </Link>
                              </TableCell>
                              <TableCell>{dev.type}</TableCell>
                              <TableCell>{dev.assetInfo?.ip || '-'}</TableCell>
                              <TableCell>{[dev.assetInfo?.os, dev.assetInfo?.osVersion].filter(Boolean).join(' ') || '-'}</TableCell>
                              <TableCell>
                                <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                                  {edrList.length === 0
                                    ? '-'
                                    : edrList.map((e, ei) => (
                                        <Chip key={ei} size="small" variant="outlined" label={e.agentName || e.vendor || 'EDR'} />
                                      ))}
                                  {isolated && <Chip size="small" color="error" label="Isolated" />}
                                </Box>
                              </TableCell>
                              <TableCell><MiniRisk score={dev.riskScore} /></TableCell>
                              <TableCell>{relationshipLabel(r.relationship)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.since ? fmt(r.since) : '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {isHostEntity && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PersonIcon color="action" />
                  <Typography variant="h6">Associated users ({associatedUsers.length})</Typography>
                </Box>
                {associatedUsers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No users associated yet. Users are linked from EDR inventory logons and from detections.
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Relationship</TableCell>
                          <TableCell>Risk</TableCell>
                          <TableCell>Detections (30d)</TableCell>
                          <TableCell>Since</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {associatedUsers.map((r, i) => {
                          const u = r.entity;
                          return (
                            <TableRow key={u._id || i}>
                              <TableCell>
                                <Link
                                  component="button"
                                  underline="hover"
                                  onClick={() => navigate(`/dashboard/entities/${u._id}`)}
                                >
                                  {u.name || u.identifier}
                                </Link>
                              </TableCell>
                              <TableCell>{relationshipLabel(r.relationship)}</TableCell>
                              <TableCell><MiniRisk score={u.riskScore} /></TableCell>
                              <TableCell>{u.securityInfo?.detectionCount30d ?? 0}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.since ? fmt(r.since) : '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>EDR coverage</Typography>
                {edrCoverageEntries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No EDR agents recorded.</Typography>
                ) : (
                  <Grid container spacing={2}>
                    {edrCoverageEntries.map(([vendorType, e]) => (
                      <Grid item xs={12} sm={6} key={vendorType}>
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5} gap={1}>
                            <Typography variant="body2" fontWeight={600}>
                              {e.agentName || e.vendor || humanize(vendorType)}
                            </Typography>
                            {e.isolated && <Chip size="small" color="error" label="Isolated" />}
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Status: {e.status || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Last seen: {e.lastSeen ? fmt(e.lastSeen) : '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" fontFamily="monospace">
                            Endpoint ID: {e.endpointId || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          )}

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PropertiesIcon color="action" />
                <Typography variant="h6">Properties</Typography>
              </Box>
              <KeyValueGrid data={entity.properties} fmt={fmt} />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AssetIcon color="action" />
                <Typography variant="h6">Asset Information</Typography>
              </Box>
              <KeyValueGrid data={entity.assetInfo} fmt={fmt} />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <SecurityIcon color="action" />
                <Typography variant="h6">Security Information</Typography>
              </Box>
              <KeyValueGrid data={entity.securityInfo} fmt={fmt} />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <IncidentIcon color="action" />
                <Typography variant="h6">Incidents ({incidents.length})</Typography>
              </Box>

              {canUpdate && (
                <Box mb={2}>
                  {incidentError && <Alert severity="error" sx={{ mb: 2 }}>{incidentError}</Alert>}
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Type"
                        placeholder="e.g. Malware, Phishing"
                        value={incidentForm.type}
                        onChange={(e) => setIncidentForm((f) => ({ ...f, type: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Severity</InputLabel>
                        <Select
                          value={incidentForm.severity}
                          label="Severity"
                          onChange={(e) => setIncidentForm((f) => ({ ...f, severity: e.target.value }))}
                        >
                          {options.severities.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Description"
                        value={incidentForm.description}
                        onChange={(e) => setIncidentForm((f) => ({ ...f, description: e.target.value }))}
                        inputProps={{ maxLength: 500 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddIncident}
                        disabled={addingIncident}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary">
                    Adding an incident raises the entity's risk score.
                  </Typography>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              )}

              {incidents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No incidents recorded.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Occurred</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {incidents.map((inc, i) => (
                      <TableRow key={inc._id || i}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(inc.occurredAt || inc.createdAt)}</TableCell>
                        <TableCell>{inc.type}</TableCell>
                        <TableCell>
                          <Chip
                            label={inc.severity || 'Unknown'}
                            size="small"
                            sx={{ backgroundColor: getSeverityColor(inc.severity).main, color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>{inc.status || '-'}</TableCell>
                        <TableCell>
                          {inc.description}
                          {personName(inc.reportedBy) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Reported by {personName(inc.reportedBy)}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Risk Score</Typography>
                {canUpdate && (
                  <Tooltip title="Recompute risk">
                    <span>
                      <IconButton size="small" onClick={handleRecomputeRisk} disabled={recomputingRisk}>
                        {recomputingRisk ? <CircularProgress size={18} /> : <RecomputeIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h3" sx={{ color: getRiskColor(riskScore) }}>
                  {riskScore}
                </Typography>
                <Box flexGrow={1}>
                  <LinearProgress
                    variant="determinate"
                    value={riskScore}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { bgcolor: riskBandColor(riskScore) },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {riskScore >= 80 ? 'Critical' : riskScore >= 60 ? 'High' : riskScore >= 40 ? 'Medium' : 'Low'} risk
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2" color="text.secondary">
                {entity.securityInfo?.detectionCount30d ?? 0} detection{(entity.securityInfo?.detectionCount30d ?? 0) === 1 ? '' : 's'} in last 30 days
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last detection: {entity.securityInfo?.lastDetectionAt ? fmt(entity.securityInfo.lastDetectionAt) : 'None'}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DetectionIcon color="action" />
                <Typography variant="h6">Related Detections ({detections.length})</Typography>
              </Box>
              {detections.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No linked detections.</Typography>
              ) : (
                <List dense disablePadding>
                  {detections.map((d) => {
                    const isPopulated = d && typeof d === 'object';
                    const detectionId = isPopulated ? d._id : d;
                    return (
                      <ListItem key={detectionId} disablePadding>
                        <ListItemButton onClick={() => navigate(`/dashboard/detections/${detectionId}`)}>
                          <ListItemText
                            primary={isPopulated ? (d.title || detectionId) : detectionId}
                            secondary={isPopulated ? [d.severity, d.status].filter(Boolean).join(' / ') : undefined}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CaseIcon color="action" />
                <Typography variant="h6">Related Cases ({cases.length})</Typography>
              </Box>
              {cases.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No linked cases.</Typography>
              ) : (
                <List dense disablePadding>
                  {cases.map((c) => (
                    <ListItem key={c._id} disablePadding>
                      <ListItemButton onClick={() => navigate(`/dashboard/cases/${c._id}`)}>
                        <ListItemText
                          primary={c.caseNumber ? `${c.caseNumber} - ${c.title || ''}` : c.title || c._id}
                          secondary={c.status}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {genericRelatedEntities.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Related Entities</Typography>
                <List dense disablePadding>
                  {genericRelatedEntities.map((r, i) => (
                    <ListItem key={r.entity._id || i} disablePadding>
                      <ListItemButton onClick={() => navigate(`/dashboard/entities/${r.entity._id}`)}>
                        <ListItemText
                          primary={r.entity.name || r.entity.identifier}
                          secondary={[r.entity.type, relationshipLabel(r.relationship)].filter(Boolean).join(' / ')}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      <EntityDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        entity={entity}
        options={options}
        onSaved={() => {
          notify('Entity updated');
          fetchEntity();
        }}
      />

      <Dialog open={deleteOpen} onClose={deleting ? undefined : () => setDeleteOpen(false)}>
        <DialogTitle>Delete Entity</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{entity.name}</strong>{' '}
            (<Box component="span" fontFamily="monospace">{entity.identifier}</Box>)? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EntityDetailPage;
